import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { presentUnexpectedFailure } from '../../../../shared/sentry/presentUnexpectedFailure';
import { useEmployeesStore } from '../../../store/useEmployeesStore'
import type { EmployeeRecord, PermissionLevel } from '../../../store/useEmployeesStore';
import { useSessionStore } from '../../../../shell/session/store/useSessionStore'
import type { ModuleKey } from '../../../../shell/session/store/sessionTypes';
import { useGlobalFeatureFlags } from '../../../../shell/providers/GlobalFeatureFlagsProvider';
import { isGlobalFeatureOn } from '../../../../shell/feature-flags/globalFeatureFlagsService';
import { planAllowsCustomer, planAllowsPromotions } from '../../../../shell/feature-flags/planEntitlementsService';
import { useEffectiveBusinessCapabilities } from '../../../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { moduleAllowedByCapabilities } from '../../../../shell/feature-flags/businessCapabilitiesService';
import { insertEmployeeBranchWithDefaults, revokeEmployeeBranchAccess, revokeEmployeeAccess, reactivateEmployeeAccess, applyPermissionPreset as applyPresetApi, MODULES } from '../../../application/employeesApi';
import {
  fetchMyInvitations,
  cancelEmployeeInvitation,
  resendEmployeeInvitation,
} from '../../../application/employeeInvitations';
import type { InvitationListItem } from '../../../application/employeeInvitations';
import { useTeamAccess } from '../../../application/useTeamAccess';
import { actorCanGrantModuleAction } from '../../../application/teamAntiEscalation';
import { mapInviteError } from '../../../application/mapInviteError';
import { mapTeamAccessError } from '../../../application/mapTeamAccessError';
import { permissionAllowsAction } from '../../../../shell/session/permissions/canModuleAction';
import { logBusinessAuditEvent } from '../../../../shell/audit/auditLogs';
import { showError, showSuccess } from '../../../../shared/ui/appToast';
import { presetToApiPayload, type RolePreset } from './EmployeeRolePresets';
import { permissionPayloadFromAssignment } from './permissionCopy';
import { describePresetPermissionDiff } from './presetPermissionDiff';
import { matchesSearchAny } from '../../../../shared/format/searchText';

export type EmployeeTab = 'active' | 'revoked' | 'invitations';

export type SortMode = 'name-asc' | 'name-desc' | 'date-asc' | 'branches-desc';
export type ViewMode = 'cards' | 'matrix';

/** Nombre de sucursal para UI; nunca devolver el UUID crudo si hay mapa. */
export function resolveBranchDisplayName(
  branchNameById: Record<string, string>,
  branchId: string,
  fallback = 'Sucursal'
): string {
  const id = branchId?.trim();
  if (!id) return fallback;
  return branchNameById[id] ?? branchNameById[id.toLowerCase()] ?? fallback;
}

export type PendingConfirm =
  | { kind: 'revoke'; employeeId: string; employeeName: string }
  | { kind: 'removeBranch'; employeeId: string; branchId: string; employeeName: string; branchName: string }
  | { kind: 'reactivate'; employeeId: string; employeeName: string }
  | { kind: 'singleBranchInfo' }
  | { kind: 'applyPreset'; employeeId: string; branchId: string; employeeName: string; branchName: string; preset: RolePreset }
  | {
      kind: 'copyFromBranch';
      employeeId: string;
      targetBranchId: string;
      sourceBranchId: string;
      employeeName: string;
      sourceBranchName: string;
      targetBranchName: string;
    }
  | {
      kind: 'copyFromEmployee';
      employeeId: string;
      branchId: string;
      sourceEmployeeId: string;
      employeeName: string;
      sourceEmployeeName: string;
      branchName: string;
    }
  | null;

export function useEmployeePermissions() {
  const {
    entLoading,
    entitlements,
    isOwner,
    canViewTeam,
    canInvite,
    canEditPermissions,
    canRevoke,
    canAssignBranches,
  } = useTeamAccess();
  const { flags: globalFeatureFlags } = useGlobalFeatureFlags();
  const includeCustomerPermissionRow =
    isGlobalFeatureOn(globalFeatureFlags, 'fiado_integrations') && planAllowsCustomer(entitlements);
  const includePromotionsPermissionRow =
    isGlobalFeatureOn(globalFeatureFlags, 'promotions_integrations') && planAllowsPromotions(entitlements);
  const includeExpensesPermissionRow = isGlobalFeatureOn(globalFeatureFlags, 'expenses_module');
  const effectiveCapabilities = useEffectiveBusinessCapabilities();
  const includePurchasesPermissionRow = effectiveCapabilities.has('purchases');

  const employees = useEmployeesStore((s) => s.employees);
  const employeesLoading = useEmployeesStore((s) => s.employeesLoading);
  const employeesError = useEmployeesStore((s) => s.employeesError);
  const loadEmployees = useEmployeesStore((s) => s.loadEmployees);
  const togglePermission = useEmployeesStore((s) => s.togglePermission);
  const togglePermissionLocal = useEmployeesStore((s) => s.togglePermissionLocal);
  const setPermissionLevel = useEmployeesStore((s) => s.setPermissionLevel);
  const setPermissionAction = useEmployeesStore((s) => s.setPermissionAction);
  const savePendingPermissions = useEmployeesStore((s) => s.savePendingPermissions);
  const setEmployees = useEmployeesStore((s) => s.setEmployees);
  const branches = useSessionStore((s) => s.branches);

  const [activeTab, setActiveTab] = useState<EmployeeTab>('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [branchFilter, setBranchFilter] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortMode>('name-asc');
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [invitations, setInvitations] = useState<InvitationListItem[]>([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [invitationsError, setInvitationsError] = useState<string | undefined>();
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
  const [pendingChanges, setPendingChanges] = useState<Set<string>>(new Set());
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [snapshotEmployees, setSnapshotEmployees] = useState<EmployeeRecord[] | null>(null);

  const pendingChangesRef = useRef(pendingChanges);
  pendingChangesRef.current = pendingChanges;

  const guardPendingChanges = useCallback((): boolean => {
    if (pendingChangesRef.current.size > 0) {
      showError(
        'Cambios sin guardar',
        'Guardá o descartá los cambios del empleado abierto antes de hacer esta acción.',
      );
      return false;
    }
    return true;
  }, []);

  const branchNameById = useMemo(() => {
    const entries: Record<string, string> = {};
    const put = (id: string | undefined, name: string | undefined) => {
      const key = id?.trim();
      const label = name?.trim();
      if (!key || !label) return;
      entries[key] = label;
      entries[key.toLowerCase()] = label;
    };
    branches.forEach((branch) => put(branch.id, branch.name));
    employees.forEach((employee) => {
      employee.branchAssignments.forEach((assignment) => {
        put(assignment.branchId, assignment.branchName);
      });
    });
    return entries;
  }, [branches, employees]);

  const loadInvitations = useCallback(async () => {
    setInvitationsLoading(true);
    setInvitationsError(undefined);
    try {
      const list = await fetchMyInvitations();
      setInvitations(list);
    } catch (e: any) {
      setInvitationsError(
        mapInviteError(e?.message, 'No se pudieron cargar las invitaciones.')
      );
      setInvitations([]);
    } finally {
      setInvitationsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canInvite || canEditPermissions) {
      void loadInvitations();
    }
  }, [canEditPermissions, canInvite, loadInvitations]);

  const visibleModules = useMemo((): ModuleKey[] => {
    return MODULES.filter(
      (m) =>
        moduleAllowedByCapabilities(m, effectiveCapabilities) &&
        (includeCustomerPermissionRow || m !== 'Customers') &&
        (includePromotionsPermissionRow || m !== 'Promotions') &&
        (includeExpensesPermissionRow || m !== 'Expenses')
    );
  }, [effectiveCapabilities, includeCustomerPermissionRow, includePromotionsPermissionRow, includeExpensesPermissionRow]);

  const activeEmployees = useMemo(() => employees.filter((e) => e.isActive !== false), [employees]);
  const revokedEmployees = useMemo(() => employees.filter((e) => e.isActive === false), [employees]);
  const pendingInvitations = useMemo(
    () => invitations.filter((i) => i.status === 'pending'),
    [invitations]
  );

  const filterAndSort = useCallback(
    (list: EmployeeRecord[]): EmployeeRecord[] => {
      let result = list;
      const q = searchQuery.trim().toLowerCase();
      if (q) {
        result = result.filter((e) => matchesSearchAny([e.name, e.email, e.role], searchQuery));
      }
      if (branchFilter) {
        result = result.filter((e) =>
          e.branchAssignments.some((a) => a.branchId === branchFilter)
        );
      }
      const sorted = [...result];
      switch (sortBy) {
        case 'name-asc':
          sorted.sort((a, b) => a.name.localeCompare(b.name));
          break;
        case 'name-desc':
          sorted.sort((a, b) => b.name.localeCompare(a.name));
          break;
        case 'date-asc':
          sorted.sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? ''));
          break;
        case 'branches-desc':
          sorted.sort((a, b) => b.branchAssignments.length - a.branchAssignments.length);
          break;
      }
      return sorted;
    },
    [searchQuery, branchFilter, sortBy]
  );

  const filteredActiveEmployees = useMemo(() => filterAndSort(activeEmployees), [filterAndSort, activeEmployees]);
  const filteredRevokedEmployees = useMemo(() => filterAndSort(revokedEmployees), [filterAndSort, revokedEmployees]);

  const runWithBusy = useCallback(
    async (key: string, fn: () => Promise<void>) => {
      setBusyKey(key);
      try {
        await fn();
        await loadEmployees({ force: true });
      } catch (e: any) {
        presentUnexpectedFailure({
          module: 'team',
          op: 'error',
          error: e,
          title: 'Error',
          userMessage: mapTeamAccessError(e?.message, 'No se pudo completar la acción.'),
        });
      } finally {
        setBusyKey(null);
      }
    },
    [loadEmployees]
  );

  const onAddBranch = useCallback(
    (employeeId: string, branchId: string, employeeName: string) => {
      if (!canAssignBranches) return;
      if (!guardPendingChanges()) return;
      void runWithBusy(`add-${employeeId}-${branchId}`, async () => {
        await insertEmployeeBranchWithDefaults(employeeId, branchId, {
          includeCustomerModule: includeCustomerPermissionRow,
          includePromotionsModule: includePromotionsPermissionRow,
          includeExpensesModule: includeExpensesPermissionRow,
          includePurchasesModule: includePurchasesPermissionRow,
        });
        await logBusinessAuditEvent('employee.branch.add', { employeeId, branchId, employeeName });
      });
    },
    [runWithBusy, canAssignBranches, guardPendingChanges, includeCustomerPermissionRow, includePromotionsPermissionRow, includeExpensesPermissionRow, includePurchasesPermissionRow]
  );

  const onRemoveBranch = useCallback(
    (employeeId: string, branchId: string, employeeName: string, canRemove: boolean) => {
      if (!canAssignBranches) return;
      if (!guardPendingChanges()) return;
      if (!canRemove) {
        setPendingConfirm({ kind: 'singleBranchInfo' });
        return;
      }
      setPendingConfirm({
        kind: 'removeBranch',
        employeeId,
        branchId,
        employeeName,
        branchName: resolveBranchDisplayName(branchNameById, branchId, 'esta sucursal'),
      });
    },
    [branchNameById, canAssignBranches, guardPendingChanges]
  );

  const onRevoke = useCallback((employeeId: string, employeeName: string) => {
    if (!canRevoke) {
      showError('Sin permiso', 'No tenés permiso para revocar acceso.');
      return;
    }
    if (!guardPendingChanges()) return;
    setPendingConfirm({ kind: 'revoke', employeeId, employeeName });
  }, [canRevoke, guardPendingChanges]);

  const onReactivate = useCallback((employeeId: string, employeeName: string) => {
    if (!canRevoke) {
      showError('Sin permiso', 'No tenés permiso para reactivar acceso.');
      return;
    }
    if (!guardPendingChanges()) return;
    setPendingConfirm({ kind: 'reactivate', employeeId, employeeName });
  }, [canRevoke, guardPendingChanges]);

  const onApplyPreset = useCallback(
    (employeeId: string, branchId: string, employeeName: string, preset: RolePreset) => {
      if (!canEditPermissions) return;
      if (!guardPendingChanges()) return;
      setPendingConfirm({
        kind: 'applyPreset',
        employeeId,
        branchId,
        employeeName,
        branchName: resolveBranchDisplayName(branchNameById, branchId, 'esta sucursal'),
        preset,
      });
    },
    [branchNameById, canEditPermissions, guardPendingChanges]
  );

  const onCopyFromBranch = useCallback(
    (
      employeeId: string,
      targetBranchId: string,
      sourceBranchId: string,
      employeeName: string
    ) => {
      if (!canEditPermissions) return;
      if (!guardPendingChanges()) return;
      if (targetBranchId === sourceBranchId) return;
      setPendingConfirm({
        kind: 'copyFromBranch',
        employeeId,
        targetBranchId,
        sourceBranchId,
        employeeName,
        sourceBranchName: resolveBranchDisplayName(branchNameById, sourceBranchId),
        targetBranchName: resolveBranchDisplayName(branchNameById, targetBranchId),
      });
    },
    [branchNameById, canEditPermissions, guardPendingChanges]
  );

  const onCopyFromEmployee = useCallback(
    (
      employeeId: string,
      branchId: string,
      sourceEmployeeId: string,
      employeeName: string
    ) => {
      if (!canEditPermissions) return;
      if (!guardPendingChanges()) return;
      if (employeeId === sourceEmployeeId) return;
      const source = employees.find((e) => e.id === sourceEmployeeId);
      setPendingConfirm({
        kind: 'copyFromEmployee',
        employeeId,
        branchId,
        sourceEmployeeId,
        employeeName,
        sourceEmployeeName: source?.name ?? 'otra persona',
        branchName: resolveBranchDisplayName(branchNameById, branchId, 'esta sucursal'),
      });
    },
    [branchNameById, canEditPermissions, employees, guardPendingChanges]
  );

  const closeConfirm = useCallback(() => {
    if (confirmLoading) return;
    setPendingConfirm(null);
  }, [confirmLoading]);

  const handleConfirmAction = useCallback(async () => {
    if (!pendingConfirm || pendingConfirm.kind === 'singleBranchInfo') {
      closeConfirm();
      return;
    }

    const runConfirmAction = async (
      busyKey: string,
      action: () => Promise<void>,
      errorMsg: string
    ) => {
      setConfirmLoading(true);
      setBusyKey(busyKey);
      try {
        await action();
        await loadEmployees({ force: true });
        setPendingConfirm(null);
      } catch (e: any) {
        presentUnexpectedFailure({
          module: 'team',
          op: 'employee-action',
          error: e,
          title: 'Error',
          userMessage: mapTeamAccessError(e?.message, errorMsg),
        });
      } finally {
        setConfirmLoading(false);
        setBusyKey(null);
      }
    };

    if (pendingConfirm.kind === 'revoke') {
      const { employeeId, employeeName } = pendingConfirm;
      await runConfirmAction(
        `revoke-${employeeId}`,
        async () => {
          await revokeEmployeeAccess(employeeId);
          await logBusinessAuditEvent('employee.revoked', { employeeId, employeeName });
        },
        'No se pudo revocar el acceso.'
      );
      return;
    }

    if (pendingConfirm.kind === 'reactivate') {
      const { employeeId, employeeName } = pendingConfirm;
      await runConfirmAction(
        `reactivate-${employeeId}`,
        async () => {
          await reactivateEmployeeAccess(employeeId);
          await logBusinessAuditEvent('employee.reactivated', { employeeId, employeeName });
        },
        'No se pudo reactivar el acceso.'
      );
      return;
    }

    if (pendingConfirm.kind === 'applyPreset') {
      const { employeeId, branchId, employeeName, preset } = pendingConfirm;
      await runConfirmAction(
        `preset-${employeeId}-${branchId}`,
        async () => {
          const payload = presetToApiPayload(preset, visibleModules);
          await applyPresetApi(employeeId, branchId, payload, {
            auditEvent: 'employee.preset.apply',
            auditMeta: {
              employeeId,
              branchId,
              employeeName,
              preset: preset.id,
              presetLabel: preset.label,
            },
          });
        },
        'No se pudo aplicar el rol.'
      );
      return;
    }

    if (pendingConfirm.kind === 'copyFromBranch') {
      const { employeeId, targetBranchId, sourceBranchId, employeeName } = pendingConfirm;
      const employee = employees.find((e) => e.id === employeeId);
      const sourceAssignment = employee?.branchAssignments.find(
        (a) => a.branchId === sourceBranchId
      );
      await runConfirmAction(
        `copy-branch-${employeeId}-${targetBranchId}-${sourceBranchId}`,
        async () => {
          const payload = permissionPayloadFromAssignment(sourceAssignment, visibleModules);
          await applyPresetApi(employeeId, targetBranchId, payload, {
            auditEvent: 'employee.permissions.copy_branch',
            auditMeta: {
              employeeId,
              targetBranchId,
              sourceBranchId,
              employeeName,
            },
          });
        },
        'No se pudieron copiar los permisos.'
      );
      return;
    }

    if (pendingConfirm.kind === 'copyFromEmployee') {
      const { employeeId, branchId, sourceEmployeeId, employeeName } = pendingConfirm;
      const source = employees.find((e) => e.id === sourceEmployeeId);
      const sourceAssignment = source?.branchAssignments.find((a) => a.branchId === branchId);
      await runConfirmAction(
        `copy-employee-${employeeId}-${branchId}-${sourceEmployeeId}`,
        async () => {
          const payload = permissionPayloadFromAssignment(sourceAssignment, visibleModules);
          await applyPresetApi(employeeId, branchId, payload, {
            auditEvent: 'employee.permissions.copy_employee',
            auditMeta: {
              employeeId,
              branchId,
              sourceEmployeeId,
              employeeName,
            },
          });
        },
        'No se pudieron copiar los permisos.'
      );
      return;
    }

    const { employeeId, branchId, employeeName } = pendingConfirm;
    await runConfirmAction(
      `rm-${employeeId}-${branchId}`,
      async () => {
        await revokeEmployeeBranchAccess(employeeId, branchId);
        await logBusinessAuditEvent('employee.branch.remove', { employeeId, branchId, employeeName });
      },
      'No se pudo quitar el acceso a la sucursal.'
    );
  }, [pendingConfirm, closeConfirm, loadEmployees, visibleModules, employees]);

  const confirmModalProps = useMemo(() => {
    if (!pendingConfirm) return null;
    if (pendingConfirm.kind === 'singleBranchInfo') {
      return {
        title: 'Única sucursal activa',
        message:
          'Este empleado solo tiene una sucursal. Para sacarlo de todo el negocio usá «Revocar acceso total».',
        confirmLabel: 'Entendido',
        destructive: false,
        iconName: 'information-circle-outline' as const,
      };
    }
    if (pendingConfirm.kind === 'revoke') {
      return {
        title: 'Revocar acceso total',
        entityName: pendingConfirm.employeeName,
        message:
          'No va a poder entrar más. El historial de ventas queda. Para volver a darle acceso hay que invitarlo de nuevo.',
        confirmLabel: 'Revocar acceso',
        destructive: true,
        iconName: 'person-remove-outline' as const,
      };
    }
    if (pendingConfirm.kind === 'reactivate') {
      return {
        title: 'Reactivar acceso',
        entityName: pendingConfirm.employeeName,
        message:
          'Vuelve a entrar con los permisos que tenía. El cambio es inmediato.',
        confirmLabel: 'Reactivar',
        destructive: false,
        iconName: 'person-add-outline' as const,
      };
    }
    if (pendingConfirm.kind === 'applyPreset') {
      const employee = employees.find((e) => e.id === pendingConfirm.employeeId);
      const assignment = employee?.branchAssignments.find(
        (a) => a.branchId === pendingConfirm.branchId
      );
      const diff = describePresetPermissionDiff(
        assignment?.permissions ?? [],
        pendingConfirm.preset,
        visibleModules
      );
      return {
        title: `Usar rol: ${pendingConfirm.preset.label}`,
        entityName: `${pendingConfirm.employeeName} · ${pendingConfirm.branchName}`,
        message: diff,
        confirmLabel: 'Usar este rol',
        destructive: false,
        iconName: 'shield-checkmark-outline' as const,
      };
    }
    if (pendingConfirm.kind === 'copyFromBranch') {
      return {
        title: 'Copiar permisos de sucursal',
        entityName: pendingConfirm.employeeName,
        message: `Se copian los permisos de ${pendingConfirm.sourceBranchName} a ${pendingConfirm.targetBranchName}. Los actuales en ${pendingConfirm.targetBranchName} se reemplazan.`,
        confirmLabel: 'Copiar permisos',
        destructive: false,
        iconName: 'copy-outline' as const,
      };
    }
    if (pendingConfirm.kind === 'copyFromEmployee') {
      return {
        title: 'Copiar permisos de otra persona',
        entityName: `${pendingConfirm.sourceEmployeeName} → ${pendingConfirm.employeeName}`,
        message: `Se copian los permisos de ${pendingConfirm.sourceEmployeeName} en ${pendingConfirm.branchName} a ${pendingConfirm.employeeName} en la misma sucursal.`,
        confirmLabel: 'Copiar permisos',
        destructive: false,
        iconName: 'copy-outline' as const,
      };
    }
    return {
      title: 'Quitar acceso a sucursal',
      entityName: pendingConfirm.branchName,
      message: `${pendingConfirm.employeeName} deja de entrar a esa sucursal. Podés volver a darle acceso después.`,
      confirmLabel: 'Quitar acceso',
      destructive: true,
      iconName: 'storefront-outline' as const,
    };
  }, [pendingConfirm, employees, visibleModules]);

  const hasActiveFilters = Boolean(
    searchQuery.trim() || branchFilter || sortBy !== 'name-asc' || (viewMode !== 'cards')
  );

  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setBranchFilter(null);
    setSortBy('name-asc');
    setViewMode('cards');
  }, []);

  const selectedEmployee = useMemo(
    () => employees.find((e) => e.id === selectedEmployeeId) ?? null,
    [employees, selectedEmployeeId]
  );

  const onOpenDetail = useCallback((employeeId: string) => {
    setSelectedEmployeeId(employeeId);
    setPendingChanges(new Set());
    setSnapshotEmployees(useEmployeesStore.getState().employees);
  }, []);

  const onCloseDetail = useCallback(() => {
    if (pendingChanges.size > 0 && !savingPermissions) {
      if (snapshotEmployees) {
        setEmployees(snapshotEmployees);
      }
      setPendingChanges(new Set());
    }
    setSelectedEmployeeId(null);
    setSnapshotEmployees(null);
  }, [pendingChanges.size, savingPermissions, snapshotEmployees, setEmployees]);

  const onCancelInvitation = useCallback(
    async (invitationId: string) => {
      if (!canInvite) {
        showError('Sin permiso', 'No tenés permiso para cancelar invitaciones.');
        return;
      }
      try {
        await cancelEmployeeInvitation(invitationId);
        showSuccess('Invitación cancelada');
        await loadInvitations();
      } catch (e: any) {
        presentUnexpectedFailure({
          module: 'team',
          op: 'error',
          error: e,
          title: 'Error',
          userMessage: mapInviteError(e?.message, 'No se pudo cancelar la invitación.'),
        });
      }
    },
    [canInvite, loadInvitations]
  );

  const onResendInvitation = useCallback(
    async (invitationId: string) => {
      if (!canInvite) {
        showError('Sin permiso', 'No tenés permiso para reenviar invitaciones.');
        return;
      }
      try {
        await resendEmployeeInvitation(invitationId);
        showSuccess('Invitación reenviada');
        await loadInvitations();
      } catch (e: any) {
        presentUnexpectedFailure({
          module: 'team',
          op: 'error',
          error: e,
          title: 'Error',
          userMessage: mapInviteError(e?.message, 'No se pudo reenviar la invitación.'),
        });
      }
    },
    [canInvite, loadInvitations]
  );

  const markPending = useCallback((branchId: string, module: ModuleKey) => {
    setPendingChanges((prev) => {
      const next = new Set(prev);
      next.add(`${branchId}:${module}`);
      return next;
    });
  }, []);

  const actorPermissionsByBranch = useMemo(() => {
    const map = new Map<string, Map<ModuleKey, (typeof branches)[0]['permissions'][0]>>();
    for (const branch of branches) {
      const byModule = new Map(branch.permissions.map((p) => [p.module, p]));
      map.set(branch.id, byModule);
    }
    return map;
  }, [branches]);

  const onTogglePermission = useCallback(
    (
      employeeId: string,
      branchId: string,
      module: ModuleKey,
      field: 'canManageAgenda' | 'canViewOthers'
    ) => {
      if (!canEditPermissions) return;
      if (!isOwner) {
        const actor = actorPermissionsByBranch.get(branchId)?.get(module);
        if (field === 'canManageAgenda' && !permissionAllowsAction(actor, 'manage_agenda')) return;
        if (field === 'canViewOthers' && !permissionAllowsAction(actor, 'view_others')) return;
      }
      togglePermissionLocal({ employeeId, branchId, module, field });
      markPending(branchId, module);
    },
    [actorPermissionsByBranch, canEditPermissions, isOwner, togglePermissionLocal, markPending]
  );

  /** Matrix toggle wrapper: gate with canEditPermissions and anti-escalation, then persist immediately. */
  const onTogglePermissionMatrix = useCallback(
    (employeeId: string, branchId: string, module: ModuleKey, field: 'canView' | 'canEdit') => {
      if (!canEditPermissions) return;
      if (!isOwner) {
        const actor = actorPermissionsByBranch.get(branchId)?.get(module);
        if (field === 'canEdit' && !permissionAllowsAction(actor, 'edit')) return;
        if (field === 'canView' && !permissionAllowsAction(actor, 'view')) return;
      }
      togglePermission({ employeeId, branchId, module, field });
    },
    [actorPermissionsByBranch, canEditPermissions, isOwner, togglePermission]
  );

  const onSetModuleLevel = useCallback(
    (employeeId: string, branchId: string, module: ModuleKey, level: PermissionLevel) => {
      if (!canEditPermissions) return;
      if (!isOwner) {
        const actor = actorPermissionsByBranch.get(branchId)?.get(module);
        if (level !== 'none' && !permissionAllowsAction(actor, 'view')) return;
        if ((level === 'edit' || level === 'full') && !permissionAllowsAction(actor, 'edit')) return;
        if ((level === 'edit-void' || level === 'edit-void-discount') && !permissionAllowsAction(actor, 'void')) {
          showError('Sin permiso', 'No podés otorgar anulación si vos no la tenés.');
          return;
        }
        if (level === 'edit-void-discount' && !permissionAllowsAction(actor, 'discount')) {
          showError('Sin permiso', 'No podés otorgar descuentos si vos no los tenés.');
          return;
        }
      }
      setPermissionLevel({ employeeId, branchId, module, level });
      markPending(branchId, module);
    },
    [actorPermissionsByBranch, canEditPermissions, isOwner, setPermissionLevel, markPending]
  );

  const onSetPermissionAction = useCallback(
    (employeeId: string, branchId: string, module: ModuleKey, action: string, enabled: boolean) => {
      if (!canEditPermissions) return;
      if (enabled && !isOwner) {
        const actor = actorPermissionsByBranch.get(branchId)?.get(module);
        if (!actorCanGrantModuleAction(actor, module, action)) {
          showError('Sin permiso', 'No podés otorgar una acción que vos no tenés.');
          return;
        }
      }
      setPermissionAction({ employeeId, branchId, module, action, enabled });
      markPending(branchId, module);
    },
    [actorPermissionsByBranch, canEditPermissions, isOwner, setPermissionAction, markPending]
  );

  const onSavePermissions = useCallback(async (): Promise<boolean> => {
    if (!canEditPermissions) return false;
    if (pendingChanges.size === 0) return false;
    const employee = selectedEmployee;
    if (!employee) return false;

    const branchModules: Record<string, ModuleKey[]> = {};
    for (const key of pendingChanges) {
      const [branchId, module] = key.split(':') as [string, ModuleKey];
      if (!branchModules[branchId]) branchModules[branchId] = [];
      branchModules[branchId].push(module);
    }

    setSavingPermissions(true);
    try {
      for (const [branchId, modules] of Object.entries(branchModules)) {
        await savePendingPermissions({ employeeId: employee.id, branchId, modules });
      }
      setPendingChanges(new Set());
      setSnapshotEmployees(useEmployeesStore.getState().employees);
      showSuccess('Permisos actualizados');
      return true;
    } catch {
      return false;
    } finally {
      setSavingPermissions(false);
    }
  }, [canEditPermissions, pendingChanges, selectedEmployee, savePendingPermissions]);

  const onDiscardChanges = useCallback(() => {
    if (snapshotEmployees) {
      setEmployees(snapshotEmployees);
    }
    setPendingChanges(new Set());
  }, [snapshotEmployees, setEmployees]);

  return {
    canViewTeam,
    canInvite,
    canEditPermissions,
    canRevoke,
    canAssignBranches,
    isOwner,
    entLoading,
    entitlements,
    employees,
    employeesLoading,
    employeesError,
    loadEmployees,
    togglePermission,
    togglePermissionSafe: onTogglePermissionMatrix,
    toggleAgendaManagement: onTogglePermission,
    setPermissionAction: onSetPermissionAction,
    setPermissionLevel: onSetModuleLevel,
    savePendingPermissions: onSavePermissions,
    hasPendingChanges: pendingChanges.size > 0,
    pendingChangesCount: pendingChanges.size,
    savingPermissions,
    onDiscardChanges,
    branches,
    branchNameById,
    visibleModules,
    activeTab,
    setActiveTab,
    searchQuery,
    setSearchQuery,
    branchFilter,
    setBranchFilter,
    sortBy,
    setSortBy,
    viewMode,
    setViewMode,
    busyKey,
    pendingConfirm,
    confirmLoading,
    confirmModalProps,
    closeConfirm,
    handleConfirmAction,
    onAddBranch,
    onRemoveBranch,
    onRevoke,
    onReactivate,
    onApplyPreset,
    onCopyFromBranch,
    onCopyFromEmployee,
    filteredActiveEmployees,
    filteredRevokedEmployees,
    invitations,
    invitationsLoading,
    invitationsError,
    loadInvitations,
    onCancelInvitation,
    onResendInvitation,
    pendingInvitations,
    includeCustomerPermissionRow,
    includePromotionsPermissionRow,
    includeExpensesPermissionRow,
    hasActiveFilters,
    clearFilters,
    selectedEmployee,
    onOpenDetail,
    onCloseDetail,
  };
}

export type EmployeePermissionsVm = ReturnType<typeof useEmployeePermissions>;
